import '@/entrypoints/application.css';
import { createElement, type ReactNode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createInertiaApp } from '@inertiajs/react';
import PublicLayout from '@/layouts/PublicLayout';
import AdminLayout from '@/layouts/AdminLayout';

const pages = import.meta.glob('../pages/**/*.tsx', { eager: true });

void createInertiaApp({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (name): any => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const page = pages[`../pages/${name}.tsx`] as any;
    if (!page) throw new Error(`Page not found: ${name}`);

    if (page.default.layout === undefined) {
      if (name.startsWith('Admin/')) {
        page.default.layout = (children: ReactNode) => createElement(AdminLayout, null, children);
      } else if (name.startsWith('Auth/')) {
        page.default.layout = null;
      } else {
        page.default.layout = (children: ReactNode) => createElement(PublicLayout, null, children);
      }
    }

    return page.default;
  },

  // Explicit setup: createInertiaApp must mount a React root that the Inertia
  // router can drive on subsequent navigations. The @inertiajs/vite plugin
  // auto-derives this only when you use its `pages: "..."` shorthand — once we
  // switched to a manual resolve for layout assignment, that derivation
  // stopped firing, the initial hydration still rendered the first page but
  // there was no live root for nav to update.
  setup({ el, App, props }) {
    if (!el) throw new Error('Inertia root element not found in DOM');
    const tree = createElement(App, props);
    if (el.hasChildNodes()) {
      hydrateRoot(el, tree);
    } else {
      createRoot(el).render(tree);
    }
  },

  strictMode: true,

  // CSP nonce from the layout's csp_meta_tag, so the <style> elements Inertia
  // injects (progress bar, error dialog) pass the style-src policy.
  nonce: document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' };
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error;
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    );
  }
});
